/**
 * Publisher Caps — operator table page (FS-822).
 *
 * A dense table-centric view of every cap defined for a publisher. Each
 * row surfaces current usage as a 3-tier colored bar:
 *
 *   - emerald: pct < 60   (healthy runway)
 *   - amber:   60 <= pct < 90  (warning band)
 *   - red-600: pct >= 90  (imminent or exceeded)
 *
 * Toolbar lets the operator search by affiliate, then filter by metric /
 * period / status. A "New Cap" button opens a slide-in drawer form that
 * POSTs to `/api/v1/publisher-caps` via the hooks layer.
 *
 * Brand: emerald/sky gradient headline, Inter, zinc borders, 6-8px rounded.
 * Light theme only. No dark mode. No emojis. No competitor references.
 */

import { useMemo, useState } from "react";
import {
	createColumnHelper,
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getSortedRowModel,
	useReactTable,
	type SortingState,
} from "@tanstack/react-table";
import { toast } from "sonner";
import {
	CAP_TYPE_LABELS,
	CAP_TYPES,
	STATUS_LABELS,
	STATUSES,
	WINDOW_LABELS,
	WINDOWS,
	capEditorSchema,
	classifyThreshold,
	filledPct,
	formatCapValue,
	type CapType,
	type PublisherCapView,
	type Status,
	type Window,
	thresholdBarClass,
	isCapType,
	isWindow,
	isStatus,
	THRESHOLD_TEXT_CLASS,
} from "./api";
import {
	usePublisherCapsCreate,
	usePublisherCapsDelete,
	usePublisherCapsList,
} from "./hooks";

// ---------------------------------------------------------------------------
// Style tokens — shared across cards / inputs / buttons. Kept aligned with
// apps/dash/DESIGN.md so the table reads like every other dash feature.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-lg border border-zinc-200 bg-white shadow-sm";
const INPUT =
	"w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const LABEL = "block text-sm font-medium text-zinc-700 mb-1";
const PRIMARY_BTN =
	"rounded-md bg-emerald-500 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50";
const GRADIENT_BTN =
	"rounded-md bg-gradient-to-r from-emerald-500 to-sky-500 px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:brightness-105";
const SECONDARY_BTN =
	"rounded-md border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50";
const DANGER_BTN =
	"rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50";
const TH =
	"px-4 py-3 text-left font-medium text-xs uppercase tracking-wider text-zinc-500";
const TD = "px-4 py-3 border-t border-zinc-200 text-sm";

const STATUS_BADGE: Record<Status, string> = {
	active: "bg-emerald-50 text-emerald-700",
	paused: "bg-zinc-100 text-zinc-600",
};

// ---------------------------------------------------------------------------
// Page props — the route passes a `?affiliateId=` deep-link through.
// ---------------------------------------------------------------------------

export interface PublisherCapsPageProps {
	initialAffiliateId?: string;
}

// ---------------------------------------------------------------------------
// Filter state.
// ---------------------------------------------------------------------------

interface Filters {
	metric: "all" | CapType;
	period: "all" | Window;
	status: "all" | Status;
}

const DEFAULT_FILTERS: Filters = {
	metric: "all",
	period: "all",
	status: "all",
};

// ===========================================================================
// Top-level component.
// ===========================================================================

export function PublisherCapsPage(props: PublisherCapsPageProps = {}) {
	const [affiliateInput, setAffiliateInput] = useState(
		props.initialAffiliateId ?? "",
	);
	const [activeAffiliate, setActiveAffiliate] = useState(
		props.initialAffiliateId ?? "",
	);
	const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
	const [drawerOpen, setDrawerOpen] = useState(false);

	const query = usePublisherCapsList(activeAffiliate);
	const allRows = query.data ?? [];

	const filteredRows = useMemo(
		() =>
			allRows.filter((row) => {
				if (filters.metric !== "all" && row.cap_type !== filters.metric) {
					return false;
				}
				if (filters.period !== "all" && row.window_key !== filters.period) {
					return false;
				}
				if (filters.status !== "all" && row.status !== filters.status) {
					return false;
				}
				return true;
			}),
		[allRows, filters],
	);

	function handleLoad(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const next = affiliateInput.trim();
		if (!next) {
			toast.error("Enter an affiliate ID first.");
			return;
		}
		setActiveAffiliate(next);
	}

	return (
		<div
			className="flex flex-col gap-6"
			data-testid="publisher-caps-page"
		>
			<PageHeader
				onNew={() => setDrawerOpen(true)}
				disableNew={activeAffiliate.trim().length === 0}
			/>
			<Toolbar
				affiliateInput={affiliateInput}
				onAffiliateChange={setAffiliateInput}
				onSubmit={handleLoad}
				filters={filters}
				onFiltersChange={setFilters}
				loading={query.isFetching}
			/>

			{activeAffiliate.trim().length === 0 ? (
				<EmptyPrompt />
			) : query.isLoading ? (
				<LoadingTable />
			) : query.isError ? (
				<ErrorBanner
					message={(query.error as Error)?.message ?? "Unable to load caps."}
				/>
			) : filteredRows.length === 0 ? (
				<EmptyResults hasFilters={hasActiveFilters(filters)} />
			) : (
				<CapsTable rows={filteredRows} />
			)}

			{drawerOpen && (
				<NewCapDrawer
					initialAffiliateId={activeAffiliate}
					onClose={() => setDrawerOpen(false)}
					onSaved={(row) => {
						setActiveAffiliate(row.affiliate_id);
						setAffiliateInput(row.affiliate_id);
						setDrawerOpen(false);
					}}
				/>
			)}
		</div>
	);
}

export default PublisherCapsPage;

function hasActiveFilters(f: Filters): boolean {
	return f.metric !== "all" || f.period !== "all" || f.status !== "all";
}

// ===========================================================================
// Page header.
// ===========================================================================

function PageHeader(props: { onNew: () => void; disableNew: boolean }) {
	return (
		<header className="flex flex-wrap items-end justify-between gap-4">
			<div className="flex flex-col gap-1">
				<h1 className="text-2xl font-semibold bg-gradient-to-r from-emerald-500 to-sky-500 bg-clip-text text-transparent">
					Publisher Caps
				</h1>
				<p className="text-sm text-zinc-500">
					Throttle each publisher's conversions, payout, or revenue ceiling
					across daily, weekly, monthly, or lifetime windows.
				</p>
			</div>
			<button
				type="button"
				className={GRADIENT_BTN}
				onClick={props.onNew}
				disabled={props.disableNew}
				data-testid="publisher-caps-new-btn"
			>
				New Cap
			</button>
		</header>
	);
}

// ===========================================================================
// Toolbar — affiliate search + filter chips.
// ===========================================================================

function Toolbar(props: {
	affiliateInput: string;
	onAffiliateChange: (v: string) => void;
	onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
	filters: Filters;
	onFiltersChange: (f: Filters) => void;
	loading: boolean;
}) {
	return (
		<form
			className={`${CARD} flex flex-wrap items-end gap-4 p-4`}
			onSubmit={props.onSubmit}
			data-testid="publisher-caps-toolbar"
		>
			<div className="flex min-w-[220px] flex-1 flex-col">
				<label htmlFor="pc-affiliate" className={LABEL}>
					Affiliate ID
				</label>
				<input
					id="pc-affiliate"
					type="text"
					autoComplete="off"
					placeholder="e.g. aff_42"
					className={INPUT}
					value={props.affiliateInput}
					onChange={(e) => props.onAffiliateChange(e.currentTarget.value)}
					data-testid="publisher-caps-affiliate-input"
				/>
			</div>

			<FilterSelect
				id="pc-metric"
				label="Metric"
				value={props.filters.metric}
				onChange={(next) =>
					props.onFiltersChange({
						...props.filters,
						metric:
							next === "all" || isCapType(next)
								? (next as Filters["metric"])
								: props.filters.metric,
					})
				}
				options={[
					{ value: "all", label: "All metrics" },
					...CAP_TYPES.map((t) => ({ value: t, label: CAP_TYPE_LABELS[t] })),
				]}
			/>

			<FilterSelect
				id="pc-period"
				label="Period"
				value={props.filters.period}
				onChange={(next) =>
					props.onFiltersChange({
						...props.filters,
						period:
							next === "all" || isWindow(next)
								? (next as Filters["period"])
								: props.filters.period,
					})
				}
				options={[
					{ value: "all", label: "All periods" },
					...WINDOWS.map((w) => ({ value: w, label: WINDOW_LABELS[w] })),
				]}
			/>

			<FilterSelect
				id="pc-status"
				label="Status"
				value={props.filters.status}
				onChange={(next) =>
					props.onFiltersChange({
						...props.filters,
						status:
							next === "all" || isStatus(next)
								? (next as Filters["status"])
								: props.filters.status,
					})
				}
				options={[
					{ value: "all", label: "Any status" },
					...STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s] })),
				]}
			/>

			<button type="submit" className={SECONDARY_BTN}>
				{props.loading ? "Loading." : "Load"}
			</button>
		</form>
	);
}

function FilterSelect(props: {
	id: string;
	label: string;
	value: string;
	onChange: (next: string) => void;
	options: Array<{ value: string; label: string }>;
}) {
	return (
		<div className="flex min-w-[140px] flex-col">
			<label htmlFor={props.id} className={LABEL}>
				{props.label}
			</label>
			<select
				id={props.id}
				className={INPUT}
				value={props.value}
				onChange={(e) => props.onChange(e.currentTarget.value)}
				data-testid={`${props.id}-select`}
			>
				{props.options.map((opt) => (
					<option key={opt.value} value={opt.value}>
						{opt.label}
					</option>
				))}
			</select>
		</div>
	);
}

// ===========================================================================
// Table.
// ===========================================================================

const columnHelper = createColumnHelper<PublisherCapView>();

function useCapColumns() {
	return useMemo(
		() => [
			columnHelper.accessor("affiliate_id", {
				header: "Affiliate",
				cell: (info) => (
					<code className="text-xs text-zinc-700">{info.getValue()}</code>
				),
			}),
			columnHelper.accessor(
				(row) => row.offer_id ?? "— all offers —",
				{
					id: "campaign",
					header: "Campaign",
					cell: (info) => (
						<span className="text-zinc-700">{info.getValue()}</span>
					),
				},
			),
			columnHelper.accessor("cap_type", {
				header: "Metric",
				cell: (info) => CAP_TYPE_LABELS[info.getValue()],
			}),
			columnHelper.accessor("window_key", {
				header: "Period",
				cell: (info) => WINDOW_LABELS[info.getValue()],
			}),
			columnHelper.accessor("cap_limit", {
				header: "Ceiling",
				cell: (info) => (
					<span className="font-medium text-zinc-900">
						{formatCapValue(info.getValue(), info.row.original.cap_type)}
					</span>
				),
			}),
			columnHelper.display({
				id: "usage",
				header: "Usage",
				cell: (info) => <UsageBarCell row={info.row.original} />,
			}),
			columnHelper.display({
				id: "pct",
				header: "% of cap",
				cell: (info) => {
					const pct = filledPct(info.row.original);
					const level = classifyThreshold(pct);
					return (
						<span
							className={`text-sm font-medium ${THRESHOLD_TEXT_CLASS[level]}`}
							data-testid="publisher-caps-pct-text"
						>
							{pct.toFixed(1)}%
						</span>
					);
				},
			}),
			columnHelper.accessor("status", {
				header: "Status",
				cell: (info) => (
					<span
						className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[info.getValue()]}`}
					>
						{STATUS_LABELS[info.getValue()]}
					</span>
				),
			}),
			columnHelper.display({
				id: "actions",
				header: "",
				cell: (info) => <RowActions row={info.row.original} />,
			}),
		],
		[],
	);
}

function CapsTable(props: { rows: PublisherCapView[] }) {
	const columns = useCapColumns();
	const [sorting, setSorting] = useState<SortingState>([]);
	const table = useReactTable({
		data: props.rows,
		columns,
		state: { sorting },
		onSortingChange: setSorting,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
	});

	return (
		<div
			className={`${CARD} overflow-hidden`}
			data-testid="publisher-caps-table"
		>
			<table className="w-full border-collapse text-sm">
				<thead className="bg-zinc-50">
					{table.getHeaderGroups().map((hg) => (
						<tr key={hg.id}>
							{hg.headers.map((h) => (
								<th key={h.id} className={TH}>
									{flexRender(h.column.columnDef.header, h.getContext())}
								</th>
							))}
						</tr>
					))}
				</thead>
				<tbody>
					{table.getRowModel().rows.map((row) => (
						<tr
							key={row.id}
							className="hover:bg-emerald-50/40 transition-colors"
							data-testid="publisher-caps-row"
						>
							{row.getVisibleCells().map((cell) => (
								<td key={cell.id} className={TD}>
									{flexRender(cell.column.columnDef.cell, cell.getContext())}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function UsageBarCell(props: { row: PublisherCapView }) {
	const pct = filledPct(props.row);
	const level = classifyThreshold(pct);
	const barClass = thresholdBarClass(pct);
	const raw = `${formatCapValue(props.row.consumed, props.row.cap_type)} / ${formatCapValue(props.row.cap_limit, props.row.cap_type)}`;

	return (
		<div className="flex items-center gap-2 min-w-[160px]">
			<div
				className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-zinc-100"
				role="progressbar"
				aria-valuemin={0}
				aria-valuemax={100}
				aria-valuenow={pct}
				aria-label={`Cap usage ${pct.toFixed(1)}%`}
				title={raw}
				data-testid="publisher-caps-usage-bar"
				data-level={level}
			>
				<div
					className={`h-full ${barClass} transition-[width] duration-200 ease-out`}
					style={{ width: `${Math.min(100, pct)}%` }}
					data-testid="publisher-caps-usage-fill"
				/>
			</div>
			<span className="whitespace-nowrap text-xs text-zinc-500" title={raw}>
				{raw}
			</span>
		</div>
	);
}

function RowActions(props: { row: PublisherCapView }) {
	const del = usePublisherCapsDelete();
	const paused = props.row.status === "paused";
	return (
		<button
			type="button"
			className={DANGER_BTN}
			disabled={paused || del.isPending}
			onClick={() => {
				del.mutate(props.row, {
					onSuccess: () => toast.success("Cap paused."),
					onError: (err: Error) =>
						toast.error(err.message || "Unable to pause cap."),
				});
			}}
			data-testid="publisher-caps-pause-btn"
		>
			{paused ? "Paused" : del.isPending ? "Pausing." : "Pause"}
		</button>
	);
}

// ===========================================================================
// Empty / loading / error states.
// ===========================================================================

function EmptyPrompt() {
	return (
		<div
			className={`${CARD} flex flex-col items-center justify-center gap-3 p-12 text-center`}
			data-testid="publisher-caps-empty-prompt"
		>
			<div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 text-lg font-semibold">
				PC
			</div>
			<h3 className="text-lg font-semibold text-zinc-900">
				Search a publisher
			</h3>
			<p className="max-w-sm text-sm text-zinc-500">
				Enter an affiliate ID above to inspect and manage its conversion,
				payout, and revenue ceilings. Use New Cap to add one.
			</p>
		</div>
	);
}

function EmptyResults(props: { hasFilters: boolean }) {
	return (
		<div
			className={`${CARD} flex flex-col items-center justify-center gap-3 p-12 text-center`}
			data-testid="publisher-caps-empty-results"
		>
			<div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 text-lg font-semibold">
				0
			</div>
			<h3 className="text-lg font-semibold text-zinc-900">
				{props.hasFilters ? "No caps match these filters" : "No caps on file"}
			</h3>
			<p className="max-w-sm text-sm text-zinc-500">
				{props.hasFilters
					? "Clear the metric / period / status filters to see every cap for this publisher."
					: "This publisher is unlimited until a cap is defined. Click New Cap to add one."}
			</p>
		</div>
	);
}

function LoadingTable() {
	return (
		<div
			className={`${CARD} p-6`}
			data-testid="publisher-caps-loading"
			aria-busy="true"
		>
			<div className="flex flex-col gap-3">
				{[0, 1, 2, 3].map((i) => (
					<div
						key={i}
						className="h-6 animate-pulse rounded bg-zinc-100"
						style={{ width: `${80 - i * 10}%` }}
					/>
				))}
			</div>
		</div>
	);
}

function ErrorBanner(props: { message: string }) {
	return (
		<div
			className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700"
			role="alert"
			data-testid="publisher-caps-error"
		>
			{props.message}
		</div>
	);
}

// ===========================================================================
// New Cap drawer.
// ===========================================================================

interface DrawerForm {
	affiliateId: string;
	offerId: string;
	capType: CapType;
	window: Window;
	limit: string;
	status: Status;
}

function NewCapDrawer(props: {
	initialAffiliateId: string;
	onClose: () => void;
	onSaved: (row: PublisherCapView) => void;
}) {
	const create = usePublisherCapsCreate();
	const [form, setForm] = useState<DrawerForm>({
		affiliateId: props.initialAffiliateId,
		offerId: "",
		capType: "conversions",
		window: "daily",
		limit: "",
		status: "active",
	});
	const [errors, setErrors] = useState<Record<string, string>>({});

	function set<K extends keyof DrawerForm>(key: K, value: DrawerForm[K]) {
		setForm((prev) => ({ ...prev, [key]: value }));
		if (errors[key]) {
			setErrors((prev) => {
				const { [key as string]: _drop, ...rest } = prev;
				return rest;
			});
		}
	}

	function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const parsed = capEditorSchema.safeParse({
			affiliateId: form.affiliateId,
			offerId: form.offerId,
			capType: form.capType,
			window: form.window,
			limit: form.limit,
			status: form.status,
		});
		if (!parsed.success) {
			setErrors(parsed.errors);
			toast.error("Please fix the highlighted fields.");
			return;
		}
		setErrors({});
		create.mutate(
			{
				affiliate_id: parsed.data.affiliate_id,
				offer_id: parsed.data.offer_id,
				cap_type: parsed.data.cap_type,
				window: parsed.data.window,
				limit: parsed.data.limit,
				status: parsed.data.status,
			},
			{
				onSuccess: (row) => {
					toast.success(`Cap saved for ${row.affiliate_id}.`);
					props.onSaved(row);
				},
				onError: (err: Error) =>
					toast.error(err.message || "Unable to save cap."),
			},
		);
	}

	return (
		<div
			className="fixed inset-0 z-50 flex"
			role="dialog"
			aria-modal="true"
			aria-labelledby="pc-drawer-title"
			data-testid="publisher-caps-drawer"
		>
			<button
				type="button"
				aria-label="Close drawer"
				className="flex-1 bg-black/40"
				onClick={props.onClose}
			/>
			<div className="relative flex h-full w-full max-w-md flex-col bg-white shadow-xl">
				<header className="flex items-center justify-between border-b border-zinc-200 p-6">
					<h2
						id="pc-drawer-title"
						className="text-lg font-semibold text-zinc-900"
					>
						New Cap
					</h2>
					<button
						type="button"
						className={SECONDARY_BTN}
						onClick={props.onClose}
					>
						Cancel
					</button>
				</header>

				<form
					className="flex flex-1 flex-col gap-4 overflow-y-auto p-6"
					onSubmit={handleSubmit}
					noValidate
				>
					<Field
						id="pc-drawer-aff"
						label="Affiliate ID"
						error={errors.affiliateId}
					>
						<input
							id="pc-drawer-aff"
							type="text"
							className={INPUT}
							autoComplete="off"
							placeholder="aff_42"
							list="pc-affiliate-options"
							value={form.affiliateId}
							onChange={(e) => set("affiliateId", e.currentTarget.value)}
							aria-invalid={Boolean(errors.affiliateId)}
						/>
					</Field>

					<Field
						id="pc-drawer-offer"
						label="Campaign / Offer ID (optional)"
						error={errors.offerId}
					>
						<input
							id="pc-drawer-offer"
							type="text"
							className={INPUT}
							autoComplete="off"
							placeholder="blank = every campaign"
							value={form.offerId}
							onChange={(e) => set("offerId", e.currentTarget.value)}
							aria-invalid={Boolean(errors.offerId)}
						/>
					</Field>

					<Field id="pc-drawer-metric" label="Metric" error={errors.capType}>
						<select
							id="pc-drawer-metric"
							className={INPUT}
							value={form.capType}
							onChange={(e) => {
								const v = e.currentTarget.value;
								if (isCapType(v)) set("capType", v);
							}}
						>
							{CAP_TYPES.map((t) => (
								<option key={t} value={t}>
									{CAP_TYPE_LABELS[t]}
								</option>
							))}
						</select>
					</Field>

					<Field id="pc-drawer-period" label="Period" error={errors.window}>
						<select
							id="pc-drawer-period"
							className={INPUT}
							value={form.window}
							onChange={(e) => {
								const v = e.currentTarget.value;
								if (isWindow(v)) set("window", v);
							}}
						>
							{WINDOWS.map((w) => (
								<option key={w} value={w}>
									{WINDOW_LABELS[w]}
								</option>
							))}
						</select>
					</Field>

					<Field id="pc-drawer-limit" label="Ceiling" error={errors.limit}>
						<input
							id="pc-drawer-limit"
							type="number"
							inputMode="numeric"
							min={1}
							step={1}
							placeholder={
								form.capType === "payout_cents"
									? "cents — e.g. 10000 = $100"
									: "e.g. 500"
							}
							className={INPUT}
							value={form.limit}
							onChange={(e) => set("limit", e.currentTarget.value)}
							aria-invalid={Boolean(errors.limit)}
						/>
					</Field>

					<Field id="pc-drawer-status" label="Status" error={errors.status}>
						<select
							id="pc-drawer-status"
							className={INPUT}
							value={form.status}
							onChange={(e) => {
								const v = e.currentTarget.value;
								if (isStatus(v)) set("status", v);
							}}
						>
							{STATUSES.map((s) => (
								<option key={s} value={s}>
									{STATUS_LABELS[s]}
								</option>
							))}
						</select>
					</Field>

					<footer className="mt-auto flex justify-end gap-3 border-t border-zinc-200 pt-4">
						<button
							type="button"
							className={SECONDARY_BTN}
							onClick={props.onClose}
							disabled={create.isPending}
						>
							Cancel
						</button>
						<button
							type="submit"
							className={PRIMARY_BTN}
							disabled={create.isPending}
							data-testid="publisher-caps-drawer-save"
						>
							{create.isPending ? "Saving." : "Save cap"}
						</button>
					</footer>
				</form>
			</div>
		</div>
	);
}

function Field(props: {
	id: string;
	label: string;
	error?: string;
	children: React.ReactNode;
}) {
	return (
		<div>
			<label htmlFor={props.id} className={LABEL}>
				{props.label}
			</label>
			{props.children}
			{props.error ? (
				<p className="text-xs text-red-600 mt-1">{props.error}</p>
			) : null}
		</div>
	);
}
